import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotificationCounts, dismissAllNotifications, NotificationCounts } from '../../services/notifications';

export default function NotificationBell() {
  const [counts, setCounts] = useState<NotificationCounts>({
    total: 0,
    messages: 0,
    quotes: 0,
    appointments: 0,
    jobs: 0,
    contracts: 0,
    wheelEntries: 0,
    wheelWins: 0
  });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchCounts = async () => {
    setLoading(true);
    const newCounts = await getNotificationCounts();
    setCounts(newCounts);
    setLoading(false);
  };

  useEffect(() => {
    fetchCounts();

    // Actualizar cada 30 segundos
    const interval = setInterval(fetchCounts, 30000);

    return () => clearInterval(interval);
  }, []);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleDismissAll = async () => {
    setDismissing(true);
    try {
      await dismissAllNotifications();
      setDismissed(true);
      await fetchCounts();
      setTimeout(() => setDismissed(false), 2500);
    } catch {
      // silencioso
    } finally {
      setDismissing(false);
    }
  };

  const handleNavigate = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const notificationItems = [
    {
      label: 'Mensajes',
      count: counts.messages,
      icon: 'ri-message-3-line',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      path: '/admin/leads'
    },
    {
      label: 'Cotizaciones',
      count: counts.quotes,
      icon: 'ri-file-list-3-line',
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      path: '/admin/precios'
    },
    {
      label: 'Citas',
      count: counts.appointments,
      icon: 'ri-calendar-check-line',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      path: '/admin/appointments'
    },
    {
      label: 'Trabajos Completados',
      count: counts.jobs,
      icon: 'ri-briefcase-line',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      path: '/admin/jobs'
    },
    {
      label: 'Contratos',
      count: counts.contracts,
      icon: 'ri-file-text-line',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      path: '/admin/contracts'
    },
    {
      label: 'Ruleta - Entradas',
      count: counts.wheelEntries,
      icon: 'ri-user-star-line',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50',
      path: '/admin/ruleta-premios'
    },
    {
      label: 'Ruleta - Premios',
      count: counts.wheelWins,
      icon: 'ri-gift-line',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      path: '/admin/ruleta-premios'
    }
  ];

  const hasNotifications = counts.total > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Botón de campanita */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
        aria-label="Notificaciones"
      >
        <i className="ri-notification-3-line text-2xl"></i>
        
        {/* Badge con contador */}
        {hasNotifications && (
          <span className="absolute top-0 right-0 flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {counts.total > 99 ? '99+' : counts.total}
          </span>
        )}
      </button>

      {/* Dropdown de notificaciones */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900 whitespace-nowrap">Notificaciones</h3>
            <div className="flex items-center gap-1">
              {hasNotifications && (
                <button
                  onClick={handleDismissAll}
                  disabled={dismissing || loading}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  title="Marcar todo como revisado"
                >
                  {dismissing ? (
                    <i className="ri-loader-4-line animate-spin"></i>
                  ) : dismissed ? (
                    <i className="ri-check-double-line text-green-500"></i>
                  ) : (
                    <i className="ri-check-double-line"></i>
                  )}
                  <span>{dismissed ? '¡Listo!' : 'Marcar todo'}</span>
                </button>
              )}
              <button
                onClick={fetchCounts}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer rounded-md hover:bg-gray-100"
                disabled={loading}
                title="Actualizar"
              >
                <i className={`ri-refresh-line text-base ${loading ? 'animate-spin' : ''}`}></i>
              </button>
            </div>
          </div>

          {/* Lista de notificaciones */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-gray-500">
                <i className="ri-loader-4-line text-2xl animate-spin"></i>
                <p className="mt-2 text-sm">Cargando...</p>
              </div>
            ) : hasNotifications ? (
              <div className="py-2">
                {notificationItems.map((item, index) => (
                  item.count > 0 && (
                    <button
                      key={index}
                      onClick={() => handleNavigate(item.path)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors cursor-pointer text-left"
                    >
                      <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${item.bgColor}`}>
                        <i className={`${item.icon} text-lg ${item.color}`}></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.label}</p>
                        <p className="text-xs text-gray-500">
                          {item.count} {item.count === 1 ? 'pendiente' : 'pendientes'}
                        </p>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-semibold ${item.bgColor} ${item.color}`}>
                        {item.count}
                      </div>
                    </button>
                  )
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                <i className="ri-checkbox-circle-line text-4xl text-green-500"></i>
                <p className="mt-2 text-sm font-medium">Todo al día</p>
                <p className="text-xs text-gray-400">No hay notificaciones pendientes</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {hasNotifications && (
            <div className="px-4 py-3 border-t border-gray-200 text-center">
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/admin/dashboard');
                }}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
              >
                Ver Dashboard
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
